<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import PlanUsageAPI from 'dashboard/api/planUsage';
import { useAlert } from 'dashboard/composables';

const { t, locale } = useI18n();

const loading = ref(true);
const usage = ref(null);
const selectedCycleId = ref('');
const glossaryOpen = ref(true);

// Intl wants BCP-47 tags (pt-BR), while app locales use underscores (pt_BR).
const intlLocale = computed(() => (locale.value || 'pt_BR').replace('_', '-'));

const formatNumber = value =>
  new Intl.NumberFormat(intlLocale.value).format(value || 0);

const formatDecimal = value =>
  new Intl.NumberFormat(intlLocale.value, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(value || 0);

const formatShortDate = dateStr => {
  if (!dateStr) return '';
  const [, month, day] = dateStr.split('-');
  return `${day}/${month}`;
};

const cycles = computed(() => usage.value?.cycles || []);

const cycleLabel = cycle => {
  const range = `${formatShortDate(cycle.cycle_start)} ${t('PLAN_USAGE.CYCLE_RANGE_TO')} ${formatShortDate(cycle.cycle_end)}`;
  return cycle.current
    ? `${t('PLAN_USAGE.CURRENT_CYCLE')} · ${range}`
    : `${t('PLAN_USAGE.CYCLE_LABEL')} ${range}`;
};

const percent = computed(() => Number(usage.value?.usage_ratio || 0));
const contactsUnlimited = computed(() => !Number(usage.value?.plan_contacts));
const messagesUnlimited = computed(() => !Number(usage.value?.message_quota));
const contactsPercent = computed(() => {
  const limit = Number(usage.value?.plan_contacts || 0);
  if (!limit) return 0;
  return Math.min(
    (Number(usage.value.unique_contacts || 0) / limit) * 100,
    100
  );
});

const usageTone = ratio => {
  if (ratio >= 90) return 'danger';
  if (ratio >= 80) return 'warn';
  return 'ok';
};

const BAR_TONE_CLASS = {
  ok: 'bg-n-brand',
  warn: 'bg-n-amber-9',
  danger: 'bg-n-ruby-9',
};
const BADGE_TONE_CLASS = {
  ok: 'bg-n-slate-3 text-n-slate-11',
  warn: 'bg-n-amber-3 text-n-amber-11',
  danger: 'bg-n-ruby-3 text-n-ruby-11',
};
const contactsTone = computed(() => usageTone(contactsPercent.value));
const franchiseTone = computed(() => usageTone(percent.value));

const freeAllowancePercent = computed(() => {
  const limit = Number(usage.value?.free_allowance || 1000);
  if (!limit) return 0;
  return Math.min((Number(usage.value?.free_first_1k || 0) / limit) * 100, 100);
});
const freeTone = computed(() => usageTone(freeAllowancePercent.value));

const franchiseTooltip = computed(() => {
  const u = usage.value || {};
  const params = {
    free: formatNumber(u.free_first_1k),
    ctwa: formatNumber(u.ctwa_excluded),
    echo: formatNumber(u.echo_excluded),
    sent: formatNumber(u.outgoing_total),
    billable: formatNumber(u.billable_messages),
    quota: formatNumber(u.message_quota),
  };
  return contactsUnlimited.value
    ? t('PLAN_USAGE.FRANCHISE_TIP_UNLIMITED_CONTACTS', params)
    : t('PLAN_USAGE.FRANCHISE_TIP', params);
});

const sentTooltip = computed(() =>
  t('PLAN_USAGE.SENT_TIP', {
    human: formatNumber(usage.value?.outgoing_human),
    ai: formatNumber(usage.value?.outgoing_ai),
    app: formatNumber(usage.value?.outgoing_app),
  })
);

const topCards = computed(() => {
  if (!usage.value) return [];
  const u = usage.value;
  return [
    {
      key: 'contacts',
      title: t('PLAN_USAGE.UNIQUE_CONTACTS'),
      tooltip: t('PLAN_USAGE.UNIQUE_CONTACTS_TIP', {
        limit: formatNumber(u.plan_contacts),
      }),
      unlimited: contactsUnlimited.value,
      badgePct: contactsPercent.value,
      tone: contactsTone.value,
      value: formatNumber(u.unique_contacts),
      suffix: contactsUnlimited.value
        ? t('PLAN_USAGE.UNLIMITED')
        : t('PLAN_USAGE.OF_CONTACTS', { limit: formatNumber(u.plan_contacts) }),
      barPercent: contactsPercent.value,
      footer: {
        label: t('PLAN_USAGE.CONTACTS_AVAILABLE_IN_CYCLE'),
        value: contactsUnlimited.value
          ? t('PLAN_USAGE.UNLIMITED')
          : formatNumber(Math.max(u.plan_contacts - u.unique_contacts, 0)),
      },
    },
    {
      key: 'franchise',
      title: t('PLAN_USAGE.FRANCHISE'),
      tooltip: franchiseTooltip.value,
      unlimited: messagesUnlimited.value,
      badgePct: percent.value,
      tone: franchiseTone.value,
      value: formatNumber(u.billable_messages),
      suffix: messagesUnlimited.value
        ? t('PLAN_USAGE.UNLIMITED')
        : t('PLAN_USAGE.OF_MESSAGES', { limit: formatNumber(u.message_quota) }),
      barPercent: Math.min(percent.value, 100),
      footer: {
        label: t('PLAN_USAGE.FRANCHISE_AVAILABLE_IN_CYCLE'),
        value: messagesUnlimited.value
          ? t('PLAN_USAGE.UNLIMITED')
          : formatNumber(u.remaining_messages),
      },
    },
    {
      key: 'free',
      title: t('PLAN_USAGE.FREE_CARD_TITLE'),
      tooltip: t('PLAN_USAGE.FREE_CARD_TIP'),
      badgePct: freeAllowancePercent.value,
      tone: freeTone.value,
      value: formatNumber(u.free_first_1k),
      suffix: t('PLAN_USAGE.OF_MESSAGES', {
        limit: formatNumber(u.free_allowance || 1000),
      }),
      barPercent: freeAllowancePercent.value,
      footer: {
        label: t('PLAN_USAGE.CTWA_CARD_TITLE'),
        tooltip: t('PLAN_USAGE.CTWA_CARD_TIP'),
        value: formatNumber(u.ctwa_excluded),
      },
    },
  ];
});

const fetchUsage = async () => {
  loading.value = true;
  try {
    const params = {};
    if (selectedCycleId.value) params.cycle_id = selectedCycleId.value;
    const { data } = await PlanUsageAPI.get(params);
    usage.value = data;
    selectedCycleId.value = data.selected_cycle_id
      ? String(data.selected_cycle_id)
      : '';
  } catch {
    useAlert(t('PLAN_USAGE.FETCH_FAILED'));
  } finally {
    loading.value = false;
  }
};

onMounted(fetchUsage);

const BAR_MAX_PX = 120;

const maxDaily = computed(() =>
  Math.max(
    1,
    ...(usage.value?.daily || []).map(item => item.unique_contacts || 0)
  )
);

const dailyBarHeight = count => {
  const value = Number(count || 0);
  if (!value) return 2;
  return Math.max(Math.round((value / maxDaily.value) * BAR_MAX_PX), 4);
};

const dailyBarTooltip = day =>
  t('PLAN_USAGE.DAILY_UNIQUES_TOOLTIP', {
    date: formatShortDate(day.day),
    count: formatNumber(day.unique_contacts),
  });

const avgMessages = computed(() => {
  const conversations = Number(usage.value?.active_conversations || 0);
  const total =
    Number(usage.value?.outgoing_total || 0) +
    Number(usage.value?.incoming || 0);
  if (!conversations) return 0;
  return total / conversations;
});

const avgAi = computed(() => {
  const conversations = Number(usage.value?.active_conversations || 0);
  if (!conversations) return 0;
  return Number(usage.value?.outgoing_ai || 0) / conversations;
});

const glossaryItems = computed(() => [
  {
    term: t('PLAN_USAGE.GLOSSARY.ITEMS.UNIQUE_CONTACT.TERM'),
    definition: t('PLAN_USAGE.GLOSSARY.ITEMS.UNIQUE_CONTACT.DEFINITION'),
  },
  {
    term: t('PLAN_USAGE.GLOSSARY.ITEMS.CONVERSATIONS.TERM'),
    definition: t('PLAN_USAGE.GLOSSARY.ITEMS.CONVERSATIONS.DEFINITION'),
  },
  {
    term: t('PLAN_USAGE.GLOSSARY.ITEMS.FRANCHISE_TIER.TERM'),
    definition: t('PLAN_USAGE.GLOSSARY.ITEMS.FRANCHISE_TIER.DEFINITION'),
  },
  {
    term: t('PLAN_USAGE.GLOSSARY.ITEMS.SENT_HUMAN.TERM'),
    definition: t('PLAN_USAGE.GLOSSARY.ITEMS.SENT_HUMAN.DEFINITION'),
  },
  {
    term: t('PLAN_USAGE.GLOSSARY.ITEMS.SENT_AI.TERM'),
    definition: t('PLAN_USAGE.GLOSSARY.ITEMS.SENT_AI.DEFINITION'),
  },
  {
    term: t('PLAN_USAGE.GLOSSARY.ITEMS.AVG_MESSAGES.TERM'),
    definition: t('PLAN_USAGE.GLOSSARY.ITEMS.AVG_MESSAGES.DEFINITION'),
  },
  {
    term: t('PLAN_USAGE.GLOSSARY.ITEMS.AVG_AI.TERM'),
    definition: t('PLAN_USAGE.GLOSSARY.ITEMS.AVG_AI.DEFINITION'),
  },
]);

const activityStats = computed(() => {
  const u = usage.value || {};
  return [
    {
      key: 'conversations',
      icon: 'i-lucide-message-square',
      label: t('PLAN_USAGE.CONVERSATIONS'),
      value: formatNumber(u.conversations),
      tooltip: t('PLAN_USAGE.CONVERSATIONS_TIP'),
    },
    {
      key: 'received',
      icon: 'i-lucide-check',
      label: t('PLAN_USAGE.RECEIVED'),
      value: formatNumber(u.incoming),
      tooltip: t('PLAN_USAGE.RECEIVED_TIP'),
    },
    {
      key: 'sent',
      icon: 'i-lucide-message-square-text',
      label: t('PLAN_USAGE.SENT'),
      value: formatNumber(u.outgoing_total),
      tooltip: sentTooltip.value,
    },
    {
      key: 'templates',
      icon: 'i-lucide-file-text',
      label: t('PLAN_USAGE.TEMPLATES'),
      value: formatNumber(u.templates_sent),
      tooltip: t('PLAN_USAGE.TEMPLATES_TIP'),
    },
    {
      key: 'avg',
      icon: 'i-lucide-message-square',
      label: t('PLAN_USAGE.AVG_PER_CONVERSATION'),
      value: formatDecimal(avgMessages.value),
      tooltip: t('PLAN_USAGE.GLOSSARY.ITEMS.AVG_MESSAGES.DEFINITION'),
    },
    {
      key: 'avgAi',
      icon: 'i-lucide-message-square',
      label: t('PLAN_USAGE.AVG_AI_PER_CONVERSATION'),
      value: formatDecimal(avgAi.value),
      tooltip: t('PLAN_USAGE.GLOSSARY.ITEMS.AVG_AI.DEFINITION'),
    },
  ];
});
</script>

<template>
  <div class="flex flex-col gap-4 pt-12 pb-16">
    <div
      v-if="!loading && usage && !usage.configured"
      class="rounded-2xl border border-n-weak bg-n-solid-1 p-8 text-center"
    >
      <h1 class="text-xl font-semibold text-n-slate-12">
        {{ usage.account_name }}
      </h1>
      <p class="mx-auto mt-2 max-w-lg text-sm text-n-slate-11">
        {{ $t('PLAN_USAGE.UNCONFIGURED') }}
      </p>
    </div>

    <template v-else-if="!loading && usage">
      <div class="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 class="text-xl font-semibold text-n-slate-12">
            {{ usage?.account_name }}
          </h1>
          <p class="mt-1 text-sm text-n-slate-11">
            {{
              contactsUnlimited
                ? $t('PLAN_USAGE.PLAN_LABEL_UNLIMITED_CONTACTS')
                : $t('PLAN_USAGE.PLAN_LABEL', {
                    contacts: formatNumber(usage?.plan_contacts),
                  })
            }}
            ·
            {{
              $t('PLAN_USAGE.QUOTA_LABEL', {
                quota: formatNumber(usage?.message_quota),
              })
            }}
          </p>
        </div>
        <label class="relative flex flex-col gap-1 text-xs text-n-slate-10">
          {{ $t('PLAN_USAGE.CYCLE_SELECT_LABEL') }}
          <span class="relative flex items-center">
            <span
              class="i-lucide-calendar pointer-events-none absolute left-2.5 size-3.5 text-n-slate-9"
            />
            <select
              v-model="selectedCycleId"
              class="appearance-none bg-none m-0 h-auto rounded-lg border border-n-weak bg-n-solid-1 py-1.5 pl-8 pr-7 text-sm text-n-slate-12"
              @change="fetchUsage"
            >
              <option v-if="!cycles.length" value="">
                {{ $t('PLAN_USAGE.CURRENT_CYCLE') }}
              </option>
              <option
                v-for="cycle in cycles"
                :key="cycle.id"
                :value="String(cycle.id)"
              >
                {{ cycleLabel(cycle) }}
              </option>
            </select>
            <span
              class="i-lucide-chevron-down pointer-events-none absolute right-2.5 size-3.5 text-n-slate-9"
            />
          </span>
        </label>
      </div>

      <div
        class="flex items-start gap-2 rounded-xl border border-n-weak bg-n-slate-2 px-4 py-3 text-sm text-n-slate-11"
      >
        <span
          class="i-lucide-info mt-0.5 size-4 flex-shrink-0 text-n-slate-9"
        />
        {{
          $t('PLAN_USAGE.FREE_ALLOWANCE', {
            count: formatNumber(usage.free_allowance || 1000),
          })
        }}
      </div>

      <div class="grid gap-4 md:grid-cols-3">
        <div
          v-for="card in topCards"
          :key="card.key"
          class="rounded-xl border border-n-weak bg-n-solid-1 p-6"
        >
          <div class="mb-3 flex items-center justify-between gap-2">
            <span
              class="flex items-center gap-1 text-sm font-semibold text-n-slate-12"
            >
              {{ card.title }}
              <span
                v-tooltip.top="{ content: card.tooltip, delay: 0 }"
                class="i-lucide-info size-3.5 cursor-help text-n-slate-9"
              />
            </span>
            <span
              v-if="!card.unlimited"
              class="flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-semibold"
              :class="BADGE_TONE_CLASS[card.tone]"
            >
              {{
                $t('PLAN_USAGE.BADGE_PCT', { pct: Math.round(card.badgePct) })
              }}
            </span>
          </div>
          <p class="mb-3 text-3xl font-semibold tracking-tight text-n-slate-12">
            {{ card.value }}
            <span class="text-base font-normal text-n-slate-11">{{
              card.suffix
            }}</span>
          </p>
          <div class="mb-3 h-2 overflow-hidden rounded-full bg-n-slate-3">
            <div
              class="h-full w-[var(--bar-percent)] rounded-full"
              :class="BAR_TONE_CLASS[card.tone]"
              :style="{ '--bar-percent': `${card.barPercent}%` }"
            />
          </div>
          <div
            class="flex items-center justify-between border-t border-n-weak pt-3 text-sm text-n-slate-11"
          >
            <span class="flex items-center gap-1">
              {{ card.footer.label }}
              <span
                v-if="card.footer.tooltip"
                v-tooltip.top="{ content: card.footer.tooltip, delay: 0 }"
                class="i-lucide-info size-3.5 cursor-help text-n-slate-9"
              />
            </span>
            <span class="font-medium text-n-slate-12">{{
              card.footer.value
            }}</span>
          </div>
        </div>
      </div>

      <div class="rounded-xl border border-n-weak bg-n-solid-1 p-6">
        <span
          class="mb-4 flex items-center gap-1 text-sm font-semibold text-n-slate-12"
        >
          {{ $t('PLAN_USAGE.DAILY_UNIQUES') }}
          <span
            v-tooltip.top="{
              content: $t('PLAN_USAGE.DAILY_UNIQUES_TIP'),
              delay: 0,
            }"
            class="i-lucide-info size-3.5 cursor-help text-n-slate-9"
          />
        </span>
        <div class="flex h-[120px] items-stretch gap-px">
          <div
            v-for="day in usage.daily"
            :key="day.day"
            v-tooltip.top="{ content: dailyBarTooltip(day), delay: 0 }"
            class="group flex min-w-0 flex-1 cursor-pointer flex-col items-center justify-end"
          >
            <div
              class="w-full max-w-[14px] rounded-t bg-n-brand h-[var(--bar-height)] group-hover:brightness-125"
              :style="{
                '--bar-height': `${dailyBarHeight(day.unique_contacts)}px`,
              }"
            />
          </div>
        </div>
        <div class="mt-1 flex gap-px">
          <span
            v-for="day in usage.daily"
            :key="day.day"
            class="min-w-0 flex-1 text-center text-[10px] leading-none text-n-slate-10"
          >
            {{ String(day.day).slice(-2) }}
          </span>
        </div>
      </div>

      <div class="grid gap-4 md:grid-cols-3">
        <div
          v-for="stat in activityStats"
          :key="stat.key"
          class="rounded-xl border border-n-weak bg-n-solid-1 px-5 py-4"
        >
          <div class="mb-3 flex items-center gap-2.5">
            <span
              class="flex size-8 flex-shrink-0 items-center justify-center rounded-lg bg-n-blue-3 text-n-blue-11"
            >
              <span :class="stat.icon" class="size-4" />
            </span>
            <span class="min-w-0 flex-1 text-sm text-n-slate-12">
              {{ stat.label }}
            </span>
            <span
              v-tooltip.top="{ content: stat.tooltip, delay: 0 }"
              class="i-lucide-circle-help size-4 flex-shrink-0 cursor-help text-n-slate-9"
            />
          </div>
          <p class="text-3xl font-semibold tracking-tight text-n-slate-12">
            {{ stat.value }}
          </p>
        </div>
      </div>

      <section
        class="overflow-hidden rounded-xl border border-n-weak bg-n-solid-1"
      >
        <button
          type="button"
          class="flex w-full items-start justify-between gap-4 px-6 py-5 text-left"
          :aria-expanded="glossaryOpen"
          aria-controls="plan-usage-glossary"
          @click="glossaryOpen = !glossaryOpen"
        >
          <span>
            <span class="block text-sm font-semibold text-n-slate-12">
              {{ $t('PLAN_USAGE.GLOSSARY.TITLE') }}
            </span>
            <span class="mt-1 block text-sm font-normal text-n-slate-11">
              {{ $t('PLAN_USAGE.GLOSSARY.SUBTITLE') }}
            </span>
          </span>
          <span
            class="i-lucide-chevron-down mt-0.5 size-4 flex-shrink-0 text-n-slate-9 transition-transform"
            :class="{ 'rotate-180': glossaryOpen }"
          />
        </button>
        <dl
          v-show="glossaryOpen"
          id="plan-usage-glossary"
          class="border-t border-n-weak"
        >
          <div
            v-for="item in glossaryItems"
            :key="item.term"
            class="grid gap-1 border-b border-n-weak px-6 py-4 last:border-b-0 md:grid-cols-[14rem_1fr] md:items-start md:gap-8"
          >
            <dt class="text-sm font-semibold text-n-slate-12">
              {{ item.term }}
            </dt>
            <dd class="text-sm text-n-slate-11">
              {{ item.definition }}
            </dd>
          </div>
        </dl>
      </section>

      <p
        class="mx-auto max-w-3xl text-center text-xs leading-5 text-n-slate-10"
      >
        {{ $t('PLAN_USAGE.FOOTER_BILLING') }}
        <br />
        {{ $t('PLAN_USAGE.FOOTER_LIVE') }}
      </p>
    </template>
  </div>
</template>
